import { css } from '@emotion/react';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Avatar, Text, PhoneOutgoingIcon } from '@frontend/design-system';

interface ProfileCardProps {
  firstName: string;
  lastName: string;
  potentialName?: string;
  shouldUsePotentialName?: boolean;
  defaultName: string;
  personID?: string | undefined;
  locationId?: string;
  showIcon?: boolean;
  markedUnread?: boolean;
  trackingId?: string;
  textSize?: 'small' | 'medium' | 'large';
}

export const ProfileCard = ({
  firstName,
  lastName,
  potentialName,
  shouldUsePotentialName,
  defaultName,
  personID,
  showIcon,
  locationId,
  markedUnread = false,
  trackingId,
  textSize = 'medium',
}: ProfileCardProps) => {
  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore('setShow');

  const handleClick = () => {
    if (personID) {
      setPersonId(personID, undefined, locationId);
      setShow(true, 'contact');
    }
  };

  const hasDefinedName = firstName || lastName;
  let displayName;

  if (hasDefinedName) {
    displayName = (
      <div data-tracking-id='phones-profile-copy-contact-name'>
        {firstName && (
          <Text as='span' size={textSize} weight='bold'>
            {firstName}
          </Text>
        )}
        {firstName && lastName && ' '}
        {lastName && (
          <Text as='span' size={textSize}>
            {lastName}
          </Text>
        )}
      </div>
    );
  } else if (potentialName && shouldUsePotentialName) {
    displayName = (
      <Text as='span' css={{ fontStyle: 'italic' }} size={textSize}>
        {potentialName}
      </Text>
    );
  } else {
    displayName = (
      <Text as='span' size={textSize}>
        {defaultName}
      </Text>
    );
  }

  return (
    <div
      data-test-id='profile-card'
      css={styles.wrapper({ isClickable: !!personID })}
      data-trackingid={trackingId}
      onClick={handleClick}
    >
      <Photos.ContactProfilePhoto
        locationId={locationId}
        personId={personID || ''}
        name={hasDefinedName && getFullName({ firstName, lastName })}
        disableClick={!personID}
      >
        {markedUnread && <Avatar.Dot variant='primary' />}
      </Photos.ContactProfilePhoto>

      <div css={styles.content}>
        {displayName}
        {showIcon && <PhoneOutgoingIcon size={16} />}
      </div>
    </div>
  );
};

const styles = {
  wrapper: ({ isClickable }: { isClickable: boolean }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    cursor: ${isClickable ? 'pointer' : 'auto'};
  `,
  content: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
};
